import { useMemo } from 'react';
import { useLocation } from '@react-router';
import { useCurationsTree } from '../useCurationsTree';
import { useCatalogRouting } from '../../useCatalogRouting';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type { ShopVideoEventProperties } from '../../Catalog.telemetry.types';
import type { UseCatalogRegistryTelemetryReturn } from '../../Catalog.telemetry';
import type { VimeoPlayerAnalyticsMetadata } from '../../utils/vimeoPlayer/analytics';
import type { GetProductCatalogCategoryBasePageEventPropsHandler } from '../../Catalog.logging.types';

type MinimalCuration = { id: string; title: Maybe<string> };

type UseTrackCurationVideoPlayerArgs = {
  getProductCatalogCategoryBasePageEventProps: GetProductCatalogCategoryBasePageEventPropsHandler;
} & Pick<
  UseCatalogRegistryTelemetryReturn,
  | 'curationVideoPlaybackStarted'
  | 'curationVideoPlaybackPaused'
  | 'curationVideoPlaybackResumed'
  | 'curationVideoPlaybackCompleted'
  | 'curationVideoContentStarted'
  | 'curationVideoContentPlaying'
  | 'curationVideoContentCompleted'
>;

export const useTrackCurationVideoPlayer = (args: UseTrackCurationVideoPlayerArgs) => {
  const { getPublicPathFromContextualUrl } = useCatalogRouting();
  const { curationsDictionary } = useCurationsTree();
  const {
    getProductCatalogCategoryBasePageEventProps,
    curationVideoPlaybackStarted,
    curationVideoPlaybackPaused,
    curationVideoPlaybackCompleted,
    curationVideoPlaybackResumed,

    curationVideoContentStarted,
    curationVideoContentPlaying,
    curationVideoContentCompleted,
  } = args;

  const location = useLocation();

  const curationFromUrl = useMemo(() => {
    const url = getPublicPathFromContextualUrl(location.pathname);
    return curationsDictionary[url];
  }, [location.pathname, curationsDictionary, getPublicPathFromContextualUrl]);

  const transformMetadataToBaseVideoProperties = useEventCallback((curation: MinimalCuration, metadata: VimeoPlayerAnalyticsMetadata): ShopVideoEventProperties => {
    const { playbackSessionId, videoId, content, playback } = metadata;
    const { categoryTelemArgs } = getProductCatalogCategoryBasePageEventProps({ curation: curationFromUrl, product: null });
    return {
      playbackSessionId,
      curationId: curation.id,
      curationTitle: curation.title,
      videoId,
      videoTitle: content.title,
      playbackPosition: playback.position,
      videoLength: playback.totalLength,
      sound: playback.sound,
      fullScreen: playback.fullScreen,
      videoPlayer: 'vimeo',
      ...categoryTelemArgs,
    };
  });

  const trackCurationVideoPlaybackStarted = useEventCallback((curation: MinimalCuration, videoMetadata: VimeoPlayerAnalyticsMetadata) => {
    curationVideoPlaybackStarted(transformMetadataToBaseVideoProperties(curation, videoMetadata));
  });
  const trackCurationVideoPlaybackPaused = useEventCallback((curation: MinimalCuration, videoMetadata: VimeoPlayerAnalyticsMetadata) => {
    curationVideoPlaybackPaused(transformMetadataToBaseVideoProperties(curation, videoMetadata));
  });
  const trackCurationVideoPlaybackCompleted = useEventCallback((curation: MinimalCuration, videoMetadata: VimeoPlayerAnalyticsMetadata) => {
    curationVideoPlaybackCompleted(transformMetadataToBaseVideoProperties(curation, videoMetadata));
  });
  const trackCurationVideoPlaybackResumed = useEventCallback((curation: MinimalCuration, videoMetadata: VimeoPlayerAnalyticsMetadata) => {
    curationVideoPlaybackResumed(transformMetadataToBaseVideoProperties(curation, videoMetadata));
  });
  const trackCurationVideoContentStarted = useEventCallback((curation: MinimalCuration, videoMetadata: VimeoPlayerAnalyticsMetadata) => {
    curationVideoContentStarted(transformMetadataToBaseVideoProperties(curation, videoMetadata));
  });
  const trackCurationVideoContentPlaying = useEventCallback((curation: MinimalCuration, videoMetadata: VimeoPlayerAnalyticsMetadata) => {
    curationVideoContentPlaying(transformMetadataToBaseVideoProperties(curation, videoMetadata));
  });
  const trackCurationVideoContentCompleted = useEventCallback((curation: MinimalCuration, videoMetadata: VimeoPlayerAnalyticsMetadata) => {
    curationVideoContentCompleted(transformMetadataToBaseVideoProperties(curation, videoMetadata));
  });

  return {
    trackCurationVideoPlaybackStarted,
    trackCurationVideoPlaybackPaused,
    trackCurationVideoPlaybackResumed,
    trackCurationVideoPlaybackCompleted,

    trackCurationVideoContentStarted,
    trackCurationVideoContentPlaying,
    trackCurationVideoContentCompleted,
  };
};
